'use client'

import { useEffect, useRef, type ReactNode } from 'react'

import { useOptionalPageAnalytics } from '@/app/context/analytics'
import type { ViewPromotionParams } from '@/app/lib/analytics/types'

/** Umbral que exige el diccionario para las impresiones (`view_promotion`). */
const IMPRESSION_THRESHOLD = 0.5

interface TrackedHeroTileProps {
  readonly promotion: ViewPromotionParams
  readonly className?: string
  readonly children: ReactNode
}

/**
 * Hoja cliente de UNA celda del hero en mosaico/banner (`HeroGaleria`):
 * - `view_promotion` la primera vez que cruza el 50% del viewport
 *   (`IntersectionObserver`, threshold 0.5; nunca en carga si está fuera de
 *   pantalla), una sola vez mientras siga montada.
 * - `select_promotion` al clicar la pieza (delegación en fase de captura; no
 *   altera ni retrasa ninguna acción de la celda, p. ej. reproducir el vídeo).
 *
 * Distinto de `TrackedPromotion`: aquella solo cuenta clics sobre un enlace real,
 * y las celdas del hero no enlazan a ningún destino. Tolerante fuera de
 * `PageAnalyticsProvider`: no emite nada. ⚠️ Pendiente de confirmar con Analítica
 * (ver `HeroGaleria.analytics.ts`).
 */
export function TrackedHeroTile({ promotion, className, children }: TrackedHeroTileProps) {
  const analytics = useOptionalPageAnalytics()
  const rootRef = useRef<HTMLDivElement | null>(null)
  const trackViewRef = useRef(analytics?.trackViewPromotion)
  const promotionRef = useRef(promotion)
  useEffect(() => {
    trackViewRef.current = analytics?.trackViewPromotion
    promotionRef.current = promotion
  })

  useEffect(() => {
    const node = rootRef.current
    if (!node) return undefined

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return
        observer.disconnect()
        trackViewRef.current?.(promotionRef.current)
      },
      { threshold: IMPRESSION_THRESHOLD },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return (
    <div ref={rootRef} className={className} onClickCapture={() => analytics?.trackSelectPromotion(promotion)}>
      {children}
    </div>
  )
}
